import type { ReactNode } from "react";
import "./Tabs.css";

export interface TabItem {
  value: string;
  label: ReactNode;
  /** small icon before the label (an <Icon /> or <img />) */
  icon?: ReactNode;
  /** number after the label, e.g. unread items */
  count?: number;
  /** colours the icon: success (green), warning (amber), danger (red) */
  tone?: "success" | "warning" | "danger";
}

interface TabsCommonProps {
  items: TabItem[];
  /** same heights as Button: sm 28, md 36 (default), lg 44 */
  size?: "sm" | "md" | "lg";
  /** label for screen readers */
  label?: string;
  /** keep one row and scroll sideways when the tabs do not fit (popups) */
  scroll?: boolean;
  /** tabs share the full width equally (e.g. two import options) */
  block?: boolean;
  className?: string;
}

export type TabsProps = TabsCommonProps &
  (
    | {
        /** default: one tab is selected (role="tablist") */
        multiple?: false;
        value: string;
        onChange: (value: string) => void;
      }
    | {
        /** several can be on at once (filters): value is a list, buttons use
            aria-pressed, a click adds or removes that value */
        multiple: true;
        value: string[];
        onChange: (value: string[]) => void;
      }
  );

/**
 * Pill tabs or filters.
 * Example: <Tabs items={[{ value: "all", label: "All" }]} value={tab} onChange={setTab} />
 * Multi-select: <Tabs multiple items={items} value={["a", "b"]} onChange={setList} />
 */
const Tabs = (props: TabsProps) => {
  const {
    items,
    size = "md",
    label,
    scroll = false,
    block = false,
    className = "",
  } = props;

  const isActive = (itemValue: string) =>
    props.multiple === true
      ? props.value.includes(itemValue)
      : props.value === itemValue;

  const select = (itemValue: string) => {
    if (props.multiple === true) {
      props.onChange(
        props.value.includes(itemValue)
          ? props.value.filter((v) => v !== itemValue)
          : [...props.value, itemValue]
      );
    } else {
      props.onChange(itemValue);
    }
  };

  return (
    <div
      className={`ds-tabs ds-tabs--${size} ${
        scroll ? "ds-tabs--scroll" : ""
      } ${block ? "ds-tabs--block" : ""} ${className}`.trim()}
      role={props.multiple === true ? "group" : "tablist"}
      aria-label={label}
    >
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          {...(props.multiple === true
            ? { "aria-pressed": isActive(item.value) }
            : { role: "tab", "aria-selected": isActive(item.value) })}
          className={`ds-tabs__tab ${isActive(item.value) ? "is-active" : ""}`}
          onClick={() => select(item.value)}
        >
          {item.icon && (
            <span
              className={`ds-tabs__icon ${
                item.tone ? `ds-tabs__icon--${item.tone}` : ""
              }`.trim()}
            >
              {item.icon}
            </span>
          )}
          {item.label}
          {item.count !== undefined && (
            <span className="ds-tabs__count">{item.count}</span>
          )}
        </button>
      ))}
    </div>
  );
};

export default Tabs;
